import type { IAlumno } from "../../../data/models/Entities";
import { EEstadoProximidad, MProximidadTutorItem } from "../../../data/models/MProximidadItem";

/**
 * Clase que representa un item de entrega, que contiene los datos de proximidad y una bandera para mostrar el grupo.
 * Esta clase se utiliza para encapsular la información necesaria para renderizar un item de entrega en la interfaz de usuario.
 *
 * Cada item debe tener los mismos Alumnos Asignados y el mismo Estado.
 *
 * @property {MProximidadTutorItem[]} data - Array de objetos MProximidadTutorItem que contiene la información de proximidad.
 * @property {boolean} showGrupo - Bandera que indica si se debe mostrar el grupo asociado a los alumnos.
 */
export class MEntregaItem {
    constructor(
        public data: MProximidadTutorItem[],
        public showGrupo: boolean = false
    ) { }

    public hasData(): boolean {
        return this.data && this.data.length > 0;
    }

    public setProximidadItems(item: MProximidadTutorItem): void {
        const existingItemIndex = this.data.findIndex(d => d === item);
        if (existingItemIndex !== -1) {
            this.data[existingItemIndex] = item;
        } else {
            this.data.push(item);
        }
    }

    public getEstadoCompartido(): EEstadoProximidad | null {
        if (!this.data || this.data.length === 0) return null;
        const firstEstado = this.data[0].Estado;
        const allSameEstado = this.data.every(item => item.Estado === firstEstado);
        return allSameEstado ? firstEstado : null;
    }

    public getAlumnosCompartidosEnClase(): IAlumno[] {
        if (!this.data || this.data.length === 0) return [];
        const mapAlumno = new Map<number, IAlumno>();
        this.data.forEach(item => {
            item.AlumnosFiltrados.forEach(alumno => {
                if (!mapAlumno.has(alumno.IdChild)) {
                    mapAlumno.set(alumno.IdChild, alumno);
                }
            });
        });
        return Array.from(mapAlumno.values());
    }

    public getTutorMasProximo(): MProximidadTutorItem | null {
        if (!this.data || this.data.length === 0) return null;
        return this.data.reduce((prev, current) => (prev.DistanciaMetros < current.DistanciaMetros) ? prev : current);
    }

    public getFechaPosicionMasReciente(): string | null {
        const tutorMasReciente = this.getTutorMasProximo();
        if (!tutorMasReciente) return null;
        return tutorMasReciente.FechaPosicion;
    }

    public getNombresTutores(): string {
        if (!this.data || this.data.length === 0) return "";
        const nombresTutores = Array.from(new Set(this.data.map(item => item.getNombreCompleto()))).join(", ");
        return nombresTutores;
    }
}